// customer-home-stays — where a stay is, as the customer's screens need it.
//
// THE RULE THAT IS THE STORY (AC3). Flow 3.3's own lead: the set of possible
// actions changes at each boundary — an ask that is routine before arrival is
// impossible during, and a deposit that mattered all week is irrelevant once
// the dogs are home. Thirteen Home states are one page reading this one
// function; a build where each screen decided for itself would disagree the
// first time a stay moved between phases at an awkward hour.
//
// STATUS LEADS; DATES ONLY SPLIT A PHASE. A dog the desk has not ticked
// *Arrived* is not staying, whatever the calendar says — dates alone would tell
// a customer their dog is in the kennel when it never came. An `arrived`
// booking past its check-out stays on collection day until the desk ticks it
// *Collected*. `today` is the kennel's Dublin business day from `/api/portal/me`,
// never the phone's clock (P2.32).
//
// OWED IS `due > 0`, NEVER THE LEDGER'S WORD. A zero-total booking is `unpaid`
// to the ledger because no money moved, and settled here because none is due —
// read `paymentStatus` and a free stay sits on Home as owed, with no amount,
// for ever (#244 review).
//
// COLLECTED AND SETTLED IS GONE (Freddi, 2026-10-09; deviation 16): the moment
// the desk ticks *Collected* on a stay with nothing owed, Home stops drawing
// it — no quiet row, no thank-you, no seven-day window. Past stays are a later
// story, and until it ships the page shows only what is still happening.
import type { BookingStatus } from "./enums";

/**
 * THE CUSTOMER SEES ONLY WHAT IS STILL TO COME (Freddi, 2026-10-12: "the
 * customer can only see future bookings, and they should not be asked for
 * deposits for pervious bookings"). A stay whose check-out day has gone is
 * past, whatever its status says: the desk's old bookings were never moved
 * through the statuses, so status alone kept a 2025 desk booking `unconfirmed`
 * and asking for its deposit. The check-out day itself is still today.
 * `/api/portal/me` drops these before they leave the server; this keeps every
 * screen agreeing with it.
 */
export function isPastStay(s: Pick<PhaseStay, "checkOut">, today: string): boolean {
  return s.checkOut < today;
}

export type StayPhase = "requested" | "before" | "staying" | "collection" | "after" | "hidden";

export interface PhaseStay {
  status: BookingStatus;
  checkIn: string; // ISO YYYY-MM-DD
  checkOut: string; // ISO YYYY-MM-DD
  /** What is still owed on the stay, in euro — zero when settled or free. */
  due: number;
}

export function stayPhase(s: PhaseStay, today: string): StayPhase {
  if (isPastStay(s, today)) return "hidden";
  switch (s.status) {
    case "requested":
      return "requested";
    case "unconfirmed":
    case "confirmed":
      return "before";
    case "arrived":
      return today < s.checkOut ? "staying" : "collection";
    case "collected":
      return s.due > 0 ? "after" : "hidden";
    default:
      return "hidden";
  }
}

/**
 * The stays Home draws, in the order it draws them — EVERY booked stay, to
 * swipe through, closest first (Freddi, 2026-10-11: "all stays booked should be
 * visible to the customer by swiping with the closest one appearing first";
 * reverses decision 69, which hid the coming stay on collection day). What is
 * in the kennel or being collected comes first, then the coming ones by
 * check-in, then a collected stay still owed (`c3l`). A settled stay is not
 * drawn at all (deviation 16).
 */
export function homeStays<T extends PhaseStay>(stays: readonly T[], today: string): T[] {
  const phased = stays.map((s) => ({ s, phase: stayPhase(s, today) })).filter((x) => x.phase !== "hidden");
  const byCheckIn = (a: T, b: T) => a.checkIn.localeCompare(b.checkIn);
  // The stay being collected today leads, then any still in (PR #255 review, #48).
  const going = phased.filter((x) => x.phase === "collection").map((x) => x.s).sort(byCheckIn);
  const staying = phased.filter((x) => x.phase === "staying").map((x) => x.s).sort(byCheckIn);
  const here = [...going, ...staying];
  const coming = phased.filter((x) => x.phase === "requested" || x.phase === "before").map((x) => x.s).sort(byCheckIn);
  const owed = phased.filter((x) => x.phase === "after").map((x) => x.s);
  return [...here, ...coming, ...owed];
}
